// Driver outstanding — what a driver still owes after settlement deductions
// exceeded the paid deposit (TRD_CHANNEL_STATUS_DAMAGE_OUTSTANDING.md Part 3).
// Debt is attached to the DRIVER, not the rental, so it survives the rental
// that created it going COMPLETED.

export enum DriverOutstandingStatus {
  OPEN = 'OPEN',
  PARTIALLY_SETTLED = 'PARTIALLY_SETTLED',
  SETTLED = 'SETTLED',
  RELEASED = 'RELEASED',
}

export const DRIVER_OUTSTANDING_STATUS_LABEL: Record<
  DriverOutstandingStatus,
  string
> = {
  [DriverOutstandingStatus.OPEN]: 'Open',
  [DriverOutstandingStatus.PARTIALLY_SETTLED]: 'Partially Settled',
  [DriverOutstandingStatus.SETTLED]: 'Settled',
  [DriverOutstandingStatus.RELEASED]: 'Released',
};

/** Statuses that still owe money — the soft-block on new rentals keys off these. */
export const OPEN_OUTSTANDING_STATUSES: DriverOutstandingStatus[] = [
  DriverOutstandingStatus.OPEN,
  DriverOutstandingStatus.PARTIALLY_SETTLED,
];

/**
 * A plain text column on the backend, not a DB enum — a new source (lost
 * asset, write-off) can land without a migration, so this map is expected to
 * grow ahead of any backend change. Unmapped values render raw.
 */
export enum DriverOutstandingSourceType {
  DAMAGE_SETTLEMENT = 'DAMAGE_SETTLEMENT',
  /** Unpaid rent days at an early return — the charges are cancelled, so the
   *  obligation lives here instead. */
  RENT_ARREARS = 'RENT_ARREARS',
}

export const DRIVER_OUTSTANDING_SOURCE_TYPE_LABEL: Record<string, string> = {
  [DriverOutstandingSourceType.DAMAGE_SETTLEMENT]: 'Kerusakan Kendaraan',
  [DriverOutstandingSourceType.RENT_ARREARS]: 'Tunggakan Sewa',
};

/** Row from `GET /rent/mgmt/v1/outstandings`. */
export interface DriverOutstanding {
  id: string;
  /** The endpoint returns uppercase `ID` (Drizzle PK casing) — normalised in
   *  the service layer, kept here so the raw response still type-checks. */
  ID?: string;
  driverID: number;
  /** Mitra identity snapshotted onto the debt row at write time — a raw
   *  driverID is not something ops can recognise. Still optional: a debt
   *  recorded for a driver with no rental snapshot to copy from has neither. */
  driverName?: string | null;
  driverCode?: string | null;
  sourceType: string;
  /** The rental this debt came from, when one exists — sourceRef has no
   *  DB-level FK, so a standalone/manually-recorded debt may carry null. */
  sourceRef: string | null;
  amountOriginal: number;
  amountOutstanding: number;
  status: DriverOutstandingStatus;
  notes: string | null;
  newRentalOverrideBy: string | null;
  newRentalOverrideReason: string | null;
  releasedBy: string | null;
  releaseReason: string | null;
  releasedAt: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface GetOutstandingsParams {
  driverID?: number;
  /** Comma-joined server-side, same convention as the rental list filters. */
  statuses?: DriverOutstandingStatus[];
}

/** A deliberate (partial) repayment — never applied automatically from a top-up. */
export interface SettleOutstandingBody {
  amount: number;
  notes?: string;
}

/** Write-off. `releaseReason` is required by the API. */
export interface ReleaseOutstandingBody {
  releaseReason: string;
}

/** Lets the driver start a new rental while this debt stays open (one-shot). */
export interface OverrideOutstandingBody {
  overrideReason: string;
}
